import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import constants from "../../Constants";
import { findVehicle, cleanTruckLabel, cleanTruckBodyLabel, capacityLabel, getTruckImage, sanitiseAiResponse, trackAiSuggestionEvent, filterWeatherDuplicateTips, filterTrafficDuplicateTips, formatDriveDuration, formatDriveDelta, splitDescriptionAndAnswers, formatQuotedWithLine, isSessionAiHandoff, markSessionAiHandoff, AI_SESSION_HANDOFF_MESSAGE } from "./helpers";

const baseURL = constants.baseURL;

const EMPTY = {
  confidence: "low",
  recommendedVehicle: null,
  recommendedReason: "",
  questions: [],
  tips: [],
  weather: null,
  traffic: null,
  handoffRecommended: false,
  handoffMessage: "",
  sessionImpressions: 0,
  sessionImpressionsCap: 6,
  sessionRemaining: 6,
};

const WHATSAPP_URL = "https://wa.me/27663784460";

// Hard rate-limit between back-end calls. LLM tokens cost money - this
// prevents rapid-fire triggers (e.g. picking a vehicle which also fires a
// /quote) from generating multiple suggestions back-to-back.
const MIN_INTERVAL_MS = 3000;

// Short debounce so rapid sibling triggers in one user action (e.g.
// vehicle-pick → requestQuote → triggerKey bump → handleSelectVehicle's
// scroll → another set) collapse into one call.
const COALESCE_MS = 350;

/**
 * QikTruck AI overlay card. Sits above the priced quote result.
 *
 * Cost control:
 *   - DOES NOT fire on its own. The parent bumps `triggerKey` whenever the
 *     user takes a real "quote action" (clicks Get Quotations, picks a
 *     different vehicle, changes address). That's the ONLY way the AI
 *     endpoint gets called - we never fire on description keystrokes.
 *   - When the parent bumps triggerKey, we wait 350ms (to coalesce sibling
 *     bumps in the same user action), then enforce a 3-second hard floor
 *     between actual fetches.
 *   - Until triggerKey > 0 the component renders nothing at all.
 *
 * UX:
 *   - "Use this instead" swaps the selected vehicle and triggers a re-quote.
 *   - Clarifying-question chips append "<question>: <answer>" to the
 *     description (without overwriting what the user already wrote).
 */
const AISuggestions = ({
  triggerKey = 0,
  description,
  truckType,
  helpers = 0,
  floors = 0,
  trips = 1,
  collectiondate = "",
  collectiontime = "",
  collection,
  delivery,
  tripDistanceMeters = 0,
  tripDurationSeconds = 0,
  vehicletypes = [],
  currentPriceCents = 0,
  otherOptions = [],
  quoteId = "",
  sessionId = "",
  source_ = "",
  pagePath = "",
  onTrackingUpdate,
  onApplyRecommendation,
  onAddDescriptionDetail,
  descriptionStale = false,
  quoteLoading = false,
  onUpdateQuote,
  onEditDescription,
  onBook,
  quotedRunners = 0,
  quotedFloors = 0,
  quotedCollectiontime = "",
}) => {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [awaitingFetch, setAwaitingFetch] = useState(false);
  const [answered, setAnswered] = useState({}); // questionId -> chip clicked OR free text
  const [customOpen, setCustomOpen] = useState({}); // questionId -> bool (free-form input visible)
  const [customText, setCustomText] = useState({}); // questionId -> current free-form draft
  // True as soon as the user has interacted with any clarifying question in
  // this AI panel. Used to surface the inline "Update quote" pill even if
  // the parent's descriptionStale flag hasn't picked up the change yet.
  const [hasInteracted, setHasInteracted] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Track the latest request so an older slow response can't overwrite a
  // newer one (race-condition guard).
  const reqIdRef = useRef(0);
  const lastFetchAtRef = useRef(0);
  const trackingIdRef = useRef(null);
  const onTrackingUpdateRef = useRef(onTrackingUpdate);
  onTrackingUpdateRef.current = onTrackingUpdate;
  const fireEventRef = useRef(null);
  const lastFetchedTriggerRef = useRef(null);

  const fireEvent = useCallback(
    (eventType, eventData = {}) => {
      trackAiSuggestionEvent({
        eventType,
        trackingId: trackingIdRef.current,
        quoteId: quoteId || undefined,
        sessionId: sessionId || undefined,
        source_: source_ || undefined,
        eventData,
      });
    },
    [quoteId, sessionId, source_],
  );
  fireEventRef.current = fireEvent;

  // Stash all "input context" in a ref so we can read it at fetch time
  // without making it part of the dep array (those changes must NOT trigger
  // calls on their own - only triggerKey bumps can).
  const ctxRef = useRef(null);
  ctxRef.current = {
    description,
    truckType,
    helpers,
    floors,
    trips,
    collectiondate,
    collectiontime,
    collection,
    delivery,
    tripDistanceMeters,
    tripDurationSeconds,
    quoteId,
    sessionId,
    source_,
    pagePath,
    triggerKey,
    currentPriceCents,
    otherOptions,
  };

  const run = useCallback(() => {
    const ctx = ctxRef.current || {};
    const trimmedDesc = (ctx.description || "").trim();
    if (trimmedDesc.length < 6) {
      setData(EMPTY);
      setAwaitingFetch(false);
      return;
    }

    if (isSessionAiHandoff(ctx.sessionId)) {
      setData((prev) => ({
        ...prev,
        questions: [],
        tips: [],
        handoffRecommended: true,
        handoffMessage: AI_SESSION_HANDOFF_MESSAGE,
        sessionRemaining: 0,
      }));
      setAwaitingFetch(false);
      setLoading(false);
      return;
    }

    const myReqId = ++reqIdRef.current;
    lastFetchAtRef.current = Date.now();
    setLoading(true);
    setRateLimited(false);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    axios
      .post(
        baseURL + "/qiktruck-caiaphas-intelligence",
        {
          description: trimmedDesc,
          truckType: ctx.truckType || "",
          helpers: ctx.helpers,
          floors: ctx.floors,
          trips: ctx.trips,
          collectiondate: ctx.collectiondate || null,
          collectiontime: ctx.collectiontime || null,
          collection: ctx.collection,
          delivery: ctx.delivery,
          distance_km: ctx.tripDistanceMeters ? ctx.tripDistanceMeters / 1000 : null,
          duration_min: ctx.tripDurationSeconds ? ctx.tripDurationSeconds / 60 : null,
          tripDistanceMeters: ctx.tripDistanceMeters || null,
          quoteId: ctx.quoteId || null,
          sessionId: ctx.sessionId || null,
          source_: ctx.source_ || null,
          pagePath: ctx.pagePath || null,
          triggerKey: ctx.triggerKey || null,
          pickedPriceCents: ctx.currentPriceCents || null,
          otherOptions: (ctx.otherOptions || []).map((o) => ({
            truckType: o.truckType,
            sellingPrice: o.sellingPrice,
          })),
        },
        { signal: controller.signal },
      )
      .then((response) => {
        if (myReqId !== reqIdRef.current) return; // stale
        const raw = response.data || EMPTY;
        const tid = raw.trackingId ?? null;
        trackingIdRef.current = tid;
        const { trackingId: _drop, ...aiPayload } = raw;
        if (aiPayload.handoffRecommended) {
          markSessionAiHandoff(ctx.sessionId);
          fireEventRef.current?.("session_handoff", {
            reason: aiPayload.handoffReason || "session_cap",
            sessionImpressions: aiPayload.sessionImpressions,
          });
        }
        setData((prev) => {
          const next = aiPayload.handoffRecommended
            ? {
                ...prev,
                ...aiPayload,
                questions: [],
                tips: [],
                handoffMessage:
                  aiPayload.handoffMessage || AI_SESSION_HANDOFF_MESSAGE,
              }
            : aiPayload;
          return sanitiseAiResponse(next);
        });
        onTrackingUpdateRef.current?.({
          trackingId: tid,
          recommendedVehicle: aiPayload.recommendedVehicle || null,
        });
        setLoading(false);
        setAwaitingFetch(false);
      })
      .catch((e) => {
        if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED") return;
        if (myReqId !== reqIdRef.current) return;
        if (e?.response?.status === 429) {
          setRateLimited(true);
          // Back off so a parent re-render cannot immediately retry.
          lastFetchAtRef.current = Date.now();
        }
        setData(EMPTY);
        setLoading(false);
        setAwaitingFetch(false);
      });
  }, []);

  const runRef = useRef(run);
  runRef.current = run;

  // The ONLY thing that triggers an AI call: a triggerKey bump from the
  // parent. Coalesce within COALESCE_MS, then defer further to honour the
  // MIN_INTERVAL_MS hard floor since the previous fetch.
  // Intentionally does NOT depend on `run` - an unstable parent callback
  // used to recreate `run` every render and re-fire this effect in a loop.
  useEffect(() => {
    if (!triggerKey) {
      lastFetchedTriggerRef.current = null;
      return undefined;
    }

    setAwaitingFetch(true);
    clearTimeout(debounceRef.current);
    const scheduleFor = triggerKey;
    const sinceLast = Date.now() - lastFetchAtRef.current;
    const wait = Math.max(COALESCE_MS, MIN_INTERVAL_MS - sinceLast);
    debounceRef.current = setTimeout(() => {
      if (lastFetchedTriggerRef.current === scheduleFor) return;
      lastFetchedTriggerRef.current = scheduleFor;
      runRef.current();
    }, wait);
    setHasInteracted(false);
    return () => clearTimeout(debounceRef.current);
  }, [triggerKey]);

  // Cancel any in-flight request on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  const visibleTips = useMemo(
    () => filterTrafficDuplicateTips(
      filterWeatherDuplicateTips(data.tips, data.weather),
      data.traffic,
    ),
    [data.tips, data.weather, data.traffic],
  );

  const trimmed = (description || "").trim();
  const { realDescription, priorAnswers } = useMemo(
    () => splitDescriptionAndAnswers(trimmed),
    [trimmed],
  );
  const quotedWithLine = useMemo(
    () =>
      formatQuotedWithLine({
        quotedRunners,
        quotedFloors,
        quotedCollectiontime,
      }),
    [quotedRunners, quotedFloors, quotedCollectiontime],
  );

  if (!triggerKey) return null;
  const hasEnoughContext = trimmed.length >= 6;
  if (!hasEnoughContext) return null;

  const recVehicle = data.recommendedVehicle
    ? findVehicle(vehicletypes, data.recommendedVehicle)
    : null;

  // Are they already on the AI's recommendation?
  const alreadyMatched =
    !!recVehicle && data.recommendedVehicle === truckType;

  // Deterministic price comparison: look up the recommended vehicle's price in
  // the already-returned /quote otherOptions. If the user is on truckType X
  // and AI recommends Y, the API typically returned a price for Y in
  // otherOptions (which prices ALL vehicles for this trip). The price for the
  // currently picked vehicle is `currentPriceCents`. Everything is in CENTS.
  let priceDeltaRands = null; // negative = saving, positive = extra cost
  if (
    recVehicle &&
    !alreadyMatched &&
    currentPriceCents > 0 &&
    Array.isArray(otherOptions)
  ) {
    const match = otherOptions.find(
      (o) => o?.truckType === data.recommendedVehicle && o?.sellingPrice > 0,
    );
    if (match?.sellingPrice > 0) {
      priceDeltaRands = Math.round(
        (match.sellingPrice - currentPriceCents) / 100,
      );
    }
  }
  const isSaving = priceDeltaRands !== null && priceDeltaRands < 0;
  const isExtra = priceDeltaRands !== null && priceDeltaRands > 0;

  const quotedVehicleLabel = cleanTruckBodyLabel(truckType);
  const recDisplayName = recVehicle
    ? recVehicle.loadgroup || cleanTruckLabel(recVehicle.type)
    : data.recommendedVehicle
      ? cleanTruckLabel(data.recommendedVehicle)
      : "";
  const handoffActive = !!data.handoffRecommended;

  // Header badge must refer to the vehicle opinion - never a floating "high
  // confidence" that reads like an endorsement of the price card above.
  const statusBadge = (() => {
    if (loading || awaitingFetch) return { kind: "thinking" };
    if (rateLimited) return { kind: "rate" };
    if (handoffActive) return { kind: "handoff" };
    if (recVehicle && alreadyMatched) {
      return {
        kind: "badge",
        text: "Matches your selection",
        confClass: "high",
      };
    }
    if (recVehicle && !alreadyMatched) {
      return {
        kind: "badge",
        text:
          data.confidence === "high"
            ? "Suggests a different vehicle"
            : "Alternative to consider",
        confClass: data.confidence === "high" ? "medium" : "low",
      };
    }
    if (data.confidence === "low") {
      return { kind: "badge", text: "Needs more info", confClass: "low" };
    }
    return null;
  })();

  const recReasonText =
    (data.recommendedReason && data.recommendedReason.trim()) ||
    (alreadyMatched
      ? "Your selected vehicle fits what you described."
      : recVehicle
        ? "Consider switching for a better fit for your load."
        : "");

  const hasRichContent =
    !!recVehicle ||
    !!data.recommendedVehicle ||
    !!(data.recommendedReason && data.recommendedReason.trim()) ||
    (data.questions || []).length > 0 ||
    visibleTips.length > 0 ||
    !!data.weather ||
    !!data.traffic ||
    !!data.confidence;

  const hasShellContent =
    !!quotedWithLine ||
    realDescription.length > 0 ||
    priorAnswers.length > 0;

  const hasAnything = hasRichContent || hasShellContent;

  // Keep the shell mounted while loading, awaiting debounce, or while the
  // parent quote is refreshing. Once a quote has fired AI (triggerKey), do not
  // collapse the whole card when the API returns a sparse payload - that made
  // the assistant vanish even though the trip was quoted successfully.
  if (
    !loading &&
    !hasAnything &&
    !awaitingFetch &&
    !quoteLoading &&
    !rateLimited &&
    !handoffActive
  ) {
    return null;
  }

  return (
    <section
      className={
        "lp2-ai" +
        (loading || awaitingFetch ? " is-loading" : "") +
        (data.confidence ? " is-conf-" + data.confidence : "")
      }
      aria-label="Load insights for your trip"
    >
      <header className="lp2-ai-head">
        <span className="lp2-ai-badge">
          <i className="mdi mdi-robot" aria-hidden="true" />
          Load insights
        </span>
        {statusBadge?.kind === "thinking" ? (
          <span className="lp2-ai-status">
            <span className="lp2-ai-dots">
              <span /><span /><span />
            </span>
            Thinking…
          </span>
        ) : statusBadge?.kind === "rate" ? (
          <span className="lp2-ai-conf lp2-ai-conf--low">
            Paused - try again shortly
          </span>
        ) : statusBadge?.kind === "handoff" ? (
          <span className="lp2-ai-conf lp2-ai-conf--medium">
            Ready for human review
          </span>
        ) : statusBadge?.kind === "badge" ? (
          <span className={"lp2-ai-conf lp2-ai-conf--" + statusBadge.confClass}>
            {statusBadge.text}
          </span>
        ) : null}
      </header>

      {!loading &&
        !awaitingFetch &&
        !handoffActive &&
        recVehicle &&
        quotedVehicleLabel && (
          <p className="lp2-ai-bridge" role="status">
            {alreadyMatched ? (
              <>
                Based on your load,{" "}
                <strong>{quotedVehicleLabel}</strong> (quoted above) looks like
                a good fit.
              </>
            ) : (
              <>
                Based on your load, we suggest{" "}
                <strong>{recDisplayName}</strong> instead of your quoted{" "}
                <strong>{quotedVehicleLabel}</strong>.
              </>
            )}
          </p>
        )}

      {!loading &&
        !awaitingFetch &&
        !handoffActive &&
        !recVehicle &&
        data.confidence === "low" && (
          <p className="lp2-ai-bridge lp2-ai-bridge--muted" role="status">
            We need a bit more detail about your load before suggesting a
            vehicle - this is separate from the price shown above.
          </p>
        )}

      {quotedWithLine && (
        <p className="lp2-ai-quoted-with" role="status">
          <i className="mdi mdi-receipt-text-outline" aria-hidden="true" />
          Quoted with: {quotedWithLine}
        </p>
      )}

      {rateLimited && !loading && !hasAnything && !handoffActive && (
        <p className="lp2-ai-rate-limit" role="status">
          <i className="mdi mdi-timer-sand" aria-hidden="true" />
          Assistant is resting briefly - update your quote again in a minute to
          refresh suggestions.
        </p>
      )}

      {handoffActive && (
        <div className="lp2-ai-handoff" role="status">
          <p className="lp2-ai-handoff-text">
            <i className="mdi mdi-account-check-outline" aria-hidden="true" />
            {data.handoffMessage || AI_SESSION_HANDOFF_MESSAGE}
          </p>
          <div className="lp2-ai-handoff-actions">
            {onBook && (
              <button
                type="button"
                className="lp2-btn lp2-btn--primary lp2-ai-handoff-book"
                onClick={() => {
                  fireEvent("handoff_book_click", {
                    sessionImpressions: data.sessionImpressions,
                  });
                  onBook();
                }}
              >
                Book this vehicle
                <i className="mdi mdi-arrow-right" aria-hidden="true" />
              </button>
            )}
            <a
              className="lp2-ai-handoff-wa"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireEvent("handoff_whatsapp_click")}
            >
              <i className="mdi mdi-whatsapp" aria-hidden="true" />
              Chat with us
            </a>
          </div>
        </div>
      )}

      {(realDescription || priorAnswers.length > 0) && (
        <div className="lp2-ai-desc-preview">
          <div className="lp2-ai-desc-preview-head">
            <span className="lp2-ai-desc-preview-label">
              <i className="mdi mdi-text-box-outline" aria-hidden="true" />
              Your load description
            </span>
            {onEditDescription && (
              <button
                type="button"
                className="lp2-ai-desc-preview-edit"
                onClick={onEditDescription}
              >
                <i className="mdi mdi-pencil-outline" aria-hidden="true" />
                Edit
              </button>
            )}
          </div>
          {realDescription ? (
            <p className="lp2-ai-desc-preview-text">{realDescription}</p>
          ) : (
            <p className="lp2-ai-desc-preview-text lp2-ai-desc-preview-text--muted">
              No free-text description yet - see your answers below.
            </p>
          )}
          {priorAnswers.length > 0 && (
            <ul className="lp2-ai-desc-chips" aria-label="Your clarifying answers">
              {priorAnswers.map((pa) => (
                <li key={`${pa.question}-${pa.answer}`} className="lp2-ai-desc-chip">
                  <span className="lp2-ai-desc-chip-q">{pa.question}</span>
                  <span className="lp2-ai-desc-chip-a">{pa.answer}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {data.weather && (
        <div
          className={
            "lp2-ai-weather" +
            (data.weather.tooFar
              ? " lp2-ai-weather--pending"
              : data.weather.severe
                ? " lp2-ai-weather--severe"
                : data.weather.willRain
                  ? " lp2-ai-weather--rain"
                  : "")
          }
        >
          <i
            className={"mdi " + (data.weather.icon || "mdi-weather-cloudy")}
            aria-hidden="true"
          />
          <div className="lp2-ai-weather-text">
            <span className="lp2-ai-weather-title">
              {data.weather.condition}
              {!data.weather.tooFar &&
              data.weather.temperatureC !== null &&
              data.weather.temperatureC !== undefined ? (
                <span className="lp2-ai-weather-temp">
                  {Math.round(data.weather.temperatureC)}°C
                </span>
              ) : null}
            </span>
            <span className="lp2-ai-weather-when">
              {data.weather.whenLabel}
              {!data.weather.tooFar && data.weather.windKph
                ? ` · ${Math.round(data.weather.windKph)} km/h wind`
                : ""}
              {!data.weather.tooFar && data.weather.precipitationMm
                ? ` · ${data.weather.precipitationMm} mm rain`
                : ""}
              {data.weather.tooFar && data.weather.daysAhead > 0
                ? ` · ${data.weather.daysAhead} days away`
                : ""}
            </span>
            {data.weather.alert && (
              <span className="lp2-ai-weather-alert">
                <i className="mdi mdi-alert-circle-outline" aria-hidden="true" />
                {data.weather.alert}
              </span>
            )}
          </div>
        </div>
      )}

      {data.traffic && (
        <div
          className={
            "lp2-ai-traffic" +
            (data.traffic.level ? ` lp2-ai-traffic--${data.traffic.level}` : "")
          }
        >
          <i
            className={"mdi " + (data.traffic.icon || "mdi-road-variant")}
            aria-hidden="true"
          />
          <div className="lp2-ai-traffic-text">
            <span className="lp2-ai-traffic-title">
              Drive time {data.traffic.hasScheduledPickup ? "at pickup" : "now"}
              <span className="lp2-ai-traffic-eta">
                {formatDriveDuration(data.traffic.trafficMin)}
              </span>
              {data.traffic.deltaMin > 3 && (
                <span className="lp2-ai-traffic-delta">
                  {formatDriveDelta(data.traffic.deltaMin)}
                </span>
              )}
            </span>
            <span className="lp2-ai-traffic-when">{data.traffic.whenLabel}</span>
            {data.traffic.alert && (
              <span className="lp2-ai-traffic-alert">
                <i className="mdi mdi-information-outline" aria-hidden="true" />
                {data.traffic.alert}
              </span>
            )}
          </div>
        </div>
      )}

      {recVehicle && recReasonText && (
        <div
          className={
            "lp2-ai-rec" + (alreadyMatched ? " lp2-ai-rec--matched" : "")
          }
        >
          <img
            className="lp2-ai-rec-img"
            src={getTruckImage(recVehicle.type)}
            alt={`${recVehicle.loadgroup || cleanTruckLabel(recVehicle.type)} truck`}
            loading="lazy"
            width={72}
            height={48}
          />
          <div className="lp2-ai-rec-text">
            <span className="lp2-ai-rec-label">
              {alreadyMatched ? "Good pick" : "Suggested vehicle"}
            </span>
            <strong className="lp2-ai-rec-name">
              {recVehicle.loadgroup || cleanTruckLabel(recVehicle.type)}
              {recVehicle.tonnes
                ? ` · ${capacityLabel(recVehicle.tonnes)}`
                : ""}
              {isSaving && (
                <span
                  className="lp2-ai-rec-delta lp2-ai-rec-delta--save"
                  title="Compared to your current pick"
                >
                  <i className="mdi mdi-trending-down" aria-hidden="true" />
                  Saves R{Math.abs(priceDeltaRands).toLocaleString("en-ZA")}
                </span>
              )}
              {isExtra && (
                <span
                  className="lp2-ai-rec-delta lp2-ai-rec-delta--extra"
                  title="Compared to your current pick"
                >
                  <i className="mdi mdi-trending-up" aria-hidden="true" />
                  +R{priceDeltaRands.toLocaleString("en-ZA")} for the right fit
                </span>
              )}
            </strong>
            <span className="lp2-ai-rec-reason">{recReasonText}</span>
            {data.nearbyAvailability && (
              <NearbyAvailability info={data.nearbyAvailability} />
            )}
          </div>
          {!alreadyMatched && (
            <button
              type="button"
              className="lp2-ai-rec-apply"
              onClick={() => {
                fireEvent("apply_recommendation", {
                  fromTruckType: truckType,
                  toTruckType: data.recommendedVehicle,
                  priceDeltaRands,
                });
                onApplyRecommendation?.(data.recommendedVehicle);
              }}
            >
              {isSaving ? "Switch & save" : "Use this instead"}
              <i className="mdi mdi-arrow-right" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {(data.questions || []).length > 0 && !handoffActive && (
        <div className="lp2-ai-questions">
          <span className="lp2-ai-questions-title">
            <i className="mdi mdi-help-circle-outline" aria-hidden="true" />
            A few quick questions so we can fine-tune your quote
          </span>
          <ul>
            {data.questions.map((q) => {
              const answered_ = answered[q.id];
              const isCustomActive =
                !!answered_ && !q.chips.includes(answered_);
              const showCustom = customOpen[q.id] || isCustomActive;
              const draft = customText[q.id] ?? (isCustomActive ? answered_ : "");
              const submitCustom = () => {
                const trimmed = (draft || "").trim();
                if (!trimmed) return;
                setAnswered((prev) => ({ ...prev, [q.id]: trimmed }));
                setHasInteracted(true);
                fireEvent("answer_question", {
                  questionId: q.id,
                  question: q.text,
                  answer: trimmed,
                  custom: true,
                });
                onAddDescriptionDetail?.(q.text, trimmed);
              };
              return (
                <li key={q.id} className="lp2-ai-question">
                  <p className="lp2-ai-question-text">{q.text}</p>
                  <div className="lp2-ai-chips">
                    {q.chips.map((chip) => {
                      const isActive = answered_ === chip;
                      return (
                        <button
                          type="button"
                          key={chip}
                          className={
                            "lp2-ai-chip" + (isActive ? " is-active" : "")
                          }
                          onClick={() => {
                            setAnswered((prev) => ({
                              ...prev,
                              [q.id]: chip,
                            }));
                            setCustomOpen((prev) => ({
                              ...prev,
                              [q.id]: false,
                            }));
                            setHasInteracted(true);
                            fireEvent("answer_question", {
                              questionId: q.id,
                              question: q.text,
                              answer: chip,
                              custom: false,
                            });
                            onAddDescriptionDetail?.(q.text, chip);
                          }}
                        >
                          {isActive ? (
                            <i
                              className="mdi mdi-check"
                              aria-hidden="true"
                            />
                          ) : null}
                          {chip}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className={
                        "lp2-ai-chip lp2-ai-chip--custom" +
                        (isCustomActive ? " is-active" : "")
                      }
                      onClick={() =>
                        setCustomOpen((prev) => ({
                          ...prev,
                          [q.id]: !prev[q.id],
                        }))
                      }
                      aria-expanded={showCustom}
                    >
                      {isCustomActive ? (
                        <i className="mdi mdi-check" aria-hidden="true" />
                      ) : (
                        <i
                          className="mdi mdi-pencil-outline"
                          aria-hidden="true"
                        />
                      )}
                      Other
                    </button>
                  </div>
                  {showCustom && (
                    <div className="lp2-ai-custom">
                      <input
                        type="text"
                        className="lp2-ai-custom-input"
                        placeholder="Tell us in your own words…"
                        value={draft}
                        onChange={(e) => {
                          // Capture the value BEFORE entering the state
                          // updater - React pools synthetic events and
                          // nulls out `e.target` once the handler returns,
                          // so reading it lazily inside `setCustomText`
                          // crashes with "Cannot read properties of null".
                          const val = e.target.value;
                          setCustomText((prev) => ({
                            ...prev,
                            [q.id]: val,
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            submitCustom();
                          }
                        }}
                        maxLength={120}
                      />
                      <button
                        type="button"
                        className="lp2-ai-custom-save"
                        onClick={submitCustom}
                        disabled={!(draft || "").trim()}
                      >
                        Add
                        <i
                          className="mdi mdi-arrow-right"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {visibleTips.length > 0 && (
        <ul className="lp2-ai-tips">
          {visibleTips.map((t, i) => (
            <li
              key={i}
              className={"lp2-ai-tip lp2-ai-tip--" + (t.color || "primary")}
            >
              <i
                className={"mdi " + (t.icon || "mdi-information-outline")}
                aria-hidden="true"
              />
              {t.text}
            </li>
          ))}
        </ul>
      )}

      {!loading &&
        !awaitingFetch &&
        !quoteLoading &&
        !rateLimited &&
        !hasRichContent && (
          <p className="lp2-ai-empty" role="status">
            No extra suggestions right now - your quote looks good to go.
          </p>
        )}

      {/* Inline "Update quote" pill - appears whenever the description has
          drifted from what was last priced OR the user has just answered a
          clarifying question (local interaction flag catches the case where
          the parent's stale detection hasn't synced yet). Saves the customer
          scrolling back up to the main quote button. */}
      {(descriptionStale || hasInteracted) && !handoffActive && (
        <div className="lp2-ai-update" role="status" aria-live="polite">
          <span className="lp2-ai-update-text">
            <i className="mdi mdi-refresh" aria-hidden="true" />
            New details added - refresh your price.
          </span>
          <button
            type="button"
            className="lp2-ai-update-btn"
            onClick={() => {
              fireEvent("update_quote_after_ai", {
                descriptionStale,
                hasInteracted,
              });
              onUpdateQuote?.();
            }}
            disabled={quoteLoading || !onUpdateQuote}
          >
            {quoteLoading ? (
              <span className="lp2-spin lp2-spin--sm" aria-hidden="true" />
            ) : (
              <i className="mdi mdi-refresh" aria-hidden="true" />
            )}
            Update quote
          </button>
        </div>
      )}

      {/* Disclaimer - always shown when the panel renders so customers know
          these are AI-generated hints, not a guarantee, and that QikTruck
          dispatch may still adjust the vehicle on the ground. */}
      <p className="lp2-ai-disclaimer">
        <i className="mdi mdi-information-outline" aria-hidden="true" />
        Assistant suggestions are generated automatically from the details
        you've shared and live conditions. Treat them as guidance - your final
        vehicle and price are confirmed when QikTruck dispatch reviews and
        accepts your booking.
      </p>
    </section>
  );
};

// Small live-availability pill rendered inside the recommendation card.
// Three visual states:
//   - substitution: AI's first pick had 0 nearby, we swapped → amber-toned note
//   - low avail: <= 2 nearby → grey, "limited" label, sets a quiet expectation
//   - healthy: 3+ nearby → teal, "X available" pill - builds trust
const NearbyAvailability = ({ info }) => {
  if (!info) return null;
  const { count, nearestKm, radiusKm, substitutedFrom, note } = info;
  const isSubstitution = !!substitutedFrom;
  const isEmpty = (count || 0) === 0;

  let toneClass = "lp2-ai-nearby--healthy";
  let icon = "mdi-map-marker-radius";
  let label;

  if (isSubstitution) {
    toneClass = "lp2-ai-nearby--swap";
    icon = "mdi-swap-horizontal-bold";
    label = `Swapped - ${count} nearby (closest ${nearestKm}km)`;
  } else if (isEmpty) {
    toneClass = "lp2-ai-nearby--gap";
    icon = "mdi-clock-outline";
    label = note || `Limited within ${radiusKm}km - dispatch from wider area`;
  } else if (count <= 2) {
    toneClass = "lp2-ai-nearby--low";
    icon = "mdi-map-marker-radius-outline";
    label = `${count} available within ${radiusKm}km${
      nearestKm != null ? ` · closest ${nearestKm}km` : ""
    }`;
  } else {
    label = `${count} available within ${radiusKm}km${
      nearestKm != null ? ` · closest ${nearestKm}km` : ""
    }`;
  }

  return (
    <span className={`lp2-ai-nearby ${toneClass}`} title="Live driver availability near pickup">
      <i className={`mdi ${icon}`} aria-hidden="true" />
      {label}
    </span>
  );
};

export default AISuggestions;
